import { Node, mergeAttributes } from "@tiptap/core"
import { ReactNodeViewRenderer } from "@tiptap/react"
import { FileNode as FileNodeComponent } from "./file-node"

export interface FileNodeOptions {
  HTMLAttributes: Record<string, unknown>
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fileNode: {
      setFileNode: (options: { src: string; fileName: string; fileSize?: number }) => ReturnType
    }
  }
}

export const FileNodeExtension = Node.create<FileNodeOptions>({
  name: "fileNode",

  group: "block",

  draggable: true,

  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-src"),
        renderHTML: (attributes) => {
          if (!attributes.src) return {}
          return { "data-src": attributes.src }
        },
      },
      fileName: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-filename"),
        renderHTML: (attributes) => {
          if (!attributes.fileName) return {}
          return { "data-filename": attributes.fileName }
        },
      },
      fileSize: {
        default: null,
        parseHTML: (element) => {
          const size = element.getAttribute("data-filesize")
          return size ? parseInt(size, 10) : null
        },
        renderHTML: (attributes) => {
          if (!attributes.fileSize) return {}
          return { "data-filesize": attributes.fileSize }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="file-node"]',
      },
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    const { src, fileName, fileSize } = node.attrs
    const ext = fileName ? fileName.split(".").pop()?.toLowerCase() || "" : ""
    const formattedSize = fileSize
      ? (() => {
          const k = 1024
          const sizes = ["Bytes", "KB", "MB", "GB"]
          const i = Math.floor(Math.log(fileSize) / Math.log(k))
          return `${parseFloat((fileSize / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
        })()
      : ""

    return [
      "div",
      mergeAttributes(
        this.options.HTMLAttributes,
        HTMLAttributes,
        { "data-type": "file-node", class: "tiptap-file-node" }
      ),
      ["div", { class: "tiptap-file-icon" }, ["span", { class: "tiptap-file-ext" }, ext]],
      [
        "div",
        { class: "tiptap-file-info" },
        ["span", { class: "tiptap-file-name" }, fileName || ""],
        formattedSize ? ["span", { class: "tiptap-file-size" }, formattedSize] : "",
      ],
      [
        "a",
        { 
          class: "tiptap-file-download", 
          href: src || "#", 
          download: fileName || "file",
          target: "_blank",
          rel: "noopener noreferrer"
        },
        "Скачать"
      ],
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(FileNodeComponent)
  },

  addCommands() {
    return {
      setFileNode:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          })
        },
    }
  },
})

export default FileNodeExtension
